/**
 * log-edit-line-items.rules.ts
 *
 * Seeds the log-edit screen's ITEMS mode with the same PO line-item check-off the
 * delivery was logged with on mobile: `CheckableLineItem[]` built from the PO's
 * lines (`GET /V1/po-data/{poNumber}`) and overlaid with what THIS log's receipt
 * rows say actually arrived.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * THE ONE THING THIS FILE EXISTS FOR: po-data's ROLLUP ALREADY CONTAINS THIS LOG
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * `PoLineItem.qtyReceived` is summed over EVERY ledger receipt on the line
 * (`PoDataHelper.cs`: `orderedLine.QtyReceived = lineReceipts.Sum(...)`), and
 * `isClosed` is `mathCloses || anyReceiptTappedCompletesLine` over that same set.
 * On the mobile SUBMIT path that set is the world BEFORE this delivery, because
 * the log does not exist yet. On this EDIT screen the log does exist, so its own
 * receipts are already inside both numbers.
 *
 * Every downstream rule assumes the submit-path meaning — `quantityReceived` is
 * "the ledger BEFORE this event":
 *
 *   - `outstandingQtyPrefill` = expected − quantityReceived − receivedThisLoad.
 *     Hand it the post-delivery rollup and this log's units are subtracted TWICE,
 *     so the shortfall reads short — usually as 0. A silently-zero shortfall on a
 *     line that is genuinely owed is precisely the class of defect this feature
 *     was built to remove.
 *   - `completeReceiptQty` = expected − quantityReceived. Same double-count, so a
 *     Complete tap would send a delta smaller than what arrived.
 *   - `isOutstandingLine` drops any row with `isClosed`. A line THIS log closed
 *     comes back from po-data closed, so it would vanish from the missing page and
 *     could never be reopened by an edit.
 *
 * So the seed does not pass po-data's numbers through. It reconstructs the PRIOR
 * ledger by subtracting this log's own contribution, which is available exactly
 * and per-line from `details.receipts` — and then every existing rule works
 * unchanged, because it is once again looking at "the world before this delivery".
 *
 * `isClosed` needs the same treatment but cannot be un-summed the same way:
 * po-data's per-line `receipts[]` carries no id and no log id (see
 * `ReceivingLogReceipt`), so a prior close cannot be identified. It can be
 * COUNTED, though: total `completesLine` taps on the line, minus this log's taps,
 * leaves whether anyone else ever closed it. Counting is enough, and it is the
 * only attribution the payload supports.
 *
 * ─────────────────────────────────────────────────────────────────────────────
 * WHAT THIS FILE DOES NOT DO
 * ─────────────────────────────────────────────────────────────────────────────
 *
 * It does not build the save. Turning edited rows back into §3.1's SPARSE receipt
 * patches is a separate problem with its own hazards (one ragged line owns several
 * receipt ids; `completesLine: false` re-opens a PO line; `isDamaged: false`
 * erases damage) and it does not belong here.
 *
 * It does not edit damage either. Damage is seeded read-only, for note fidelity
 * and display: `damagedQuantity` silently rewrites `isDamaged` on the wire, so
 * changing it needs its own deliberate flow.
 *
 * When po-data is unavailable (a PO-less log, a failed fetch), the caller falls
 * back to `receiptRowsToNoteLines` in `log-edit-items-string.rules.ts`. That
 * inverter deliberately stamps `isClosed: true` and blanks the expected
 * quantities so no shortfall math can run on data it does not have — which is
 * correct for recomposing the note and means the fallback is a DEGRADED check-off
 * with no missing-items math, never a locked field with nowhere to go.
 */

import {
  CheckableLineItem,
  LineDamage,
  LineItemStatus,
  effectiveExpectedQty,
  lineKey,
} from '../../../service/receiving-mobile/log-entry-draft.store';
import { PoLineItem } from '../../../models/poData/PoLineItem';
import { ReceivingLogEditReceipt } from '../../../models/logs/ReceivingLogDetails';
import { damageFor } from './log-edit-items-string.rules';

/**
 * What ONE PO line's receipt rows on THIS log add up to. Everything the seed needs
 * to both back this log out of the PO rollup and re-describe what it received.
 */
export interface LogLineContribution {
  /**
   * Units this log BOOKED against the line — the stored `quantityReceived` sum,
   * which is NET of any refused/returned damaged units (`applyDamage` deducted
   * them at submit). Null-quantity (part/status-only) rows count 0.
   *
   * This is the number to take back out of po-data's rollup, because that rollup
   * sums the same stored column.
   */
  received: number;

  /**
   * Units that physically ARRIVED on the line this delivery: {@link received}
   * plus the damaged units that were refused or returned.
   *
   * This is the number the check-off shows and round-trips, because the damage is
   * fed back in separately through `damageByLine` and `applyDamage` subtracts
   * those units again. Seeding the NET amount here and then re-applying the damage
   * would deduct them twice and understate what arrived — the same trap
   * `receiptRowsToNoteLines` documents.
   */
  arrived: number;
  /** How many of this log's rows tapped `completesLine`. Subtracted from the line's total taps. */
  closingTaps: number;
  /** True when any of this log's rows carries a real arrival status — i.e. something arrived. */
  hasArrival: boolean;
  /** The strongest arrival status across this log's rows; `'partial'` wins over `'complete'`. */
  status: LineItemStatus;
  /** Part descriptions from this log's null-quantity rows — a ragged line's entry, in order. */
  parts: string[];
  /** This log's rows on the line, in payload order. Kept so damage can be summarised from them. */
  rows: ReceivingLogEditReceipt[];
}

/** Per-PO-line contributions from this log, keyed by `poLineSequence`. Off-PO rows are excluded. */
export type LogContributionsByLine = ReadonlyMap<number, LogLineContribution>;

/**
 * Sums this log's receipt rows per PO line.
 *
 * Off-PO rows (`poLineSequence == null`) are deliberately left out — they are not
 * a PO line's business and become their own seeded rows via
 * {@link seedOffPoRows}.
 */
export function logContributionsByLine(
  receipts: readonly ReceivingLogEditReceipt[] | null | undefined,
): LogContributionsByLine {
  const byLine = new Map<number, LogLineContribution>();

  for (const row of receipts ?? []) {
    if (row.poLineSequence == null) { continue; }

    const entry = byLine.get(row.poLineSequence) ?? {
      received: 0,
      arrived: 0,
      closingTaps: 0,
      hasArrival: false,
      status: null as LineItemStatus,
      parts: [],
      rows: [],
    };

    const net = Number(row.quantityReceived ?? 0) || 0;
    entry.received += net;
    // Refused/returned units were deducted from the stored quantity at submit, so
    // add them back to recover what arrived. See LogLineContribution.arrived.
    entry.arrived += net + (
      row.damageDisposition === 'Refuse' || row.damageDisposition === 'Return'
        ? Number(row.damagedQuantity ?? 0) || 0
        : 0
    );
    if (row.completesLine === true) { entry.closingTaps++; }
    if (row.receiptStatus != null) { entry.hasArrival = true; }
    // Partial wins: a line this log split across both statuses still arrived in part.
    if (row.receiptStatus === 'Partial') {
      entry.status = 'partial';
    } else if (row.receiptStatus === 'Complete' && entry.status == null) {
      entry.status = 'complete';
    }
    // A null quantity is a PART row, not a zero — its description IS the entry.
    if (row.quantityReceived == null) {
      const part = (row.itemDescription ?? '').trim();
      if (part.length > 0) { entry.parts.push(part); }
    }
    entry.rows.push(row);

    byLine.set(row.poLineSequence, entry);
  }

  return byLine;
}

/** The inputs a seed needs. Grouped so a caller cannot get the two arrays the wrong way round. */
export interface LineItemSeedInput {
  /** The PO's lines, straight off `FormattedPoValues.lineItems`. */
  lineItems: readonly PoLineItem[] | null | undefined;
  /** THIS log's receipt rows, straight off the details payload. */
  logReceipts: readonly ReceivingLogEditReceipt[] | null | undefined;
  /**
   * Whether this log is a Project delivery, which treats ERP-canceled PO lines as
   * receivable. Derived from po-data's `jobInformation.jobType` via
   * `logTypeFromJobType` — the log row itself does not record it.
   */
  isProject: boolean;
}

/**
 * The PO's lines as an editable check-off, showing what THIS log received.
 *
 * Each row's `quantityReceived` is the PRIOR ledger (po-data's rollup minus this
 * log's own units) and its `isClosed` is the prior close — see the file header for
 * why passing po-data's values through is a correctness bug and not a shortcut.
 * `status`/`receivedQty`/`partsReceived`/`completesLine` describe this log, so the
 * screen opens on the delivery as it was logged rather than on an empty grid.
 *
 * Rows are returned in PO line order, which is the order the note lists them in.
 */
export function seedLineItemsFromPo(input: LineItemSeedInput): CheckableLineItem[] {
  const contributions = logContributionsByLine(input.logReceipts);

  return (input.lineItems ?? []).map((li) => {
    const mine = contributions.get(li.poLineSequence);
    const isCanceled = input.isProject ? false : li.isCanceled;

    // Un-cancelling for a Project receives the line in full, so the effective
    // expected reverts to the ordered qty (the ERP expected is 0 on a fully
    // canceled line). Same derivation the mobile Items step uses.
    const quantityExpected = effectiveExpectedQty(
      { quantityOrderedOriginal: li.qtyOrdered, quantityExpectedOriginal: li.qtyExpected, isCanceledOriginal: li.isCanceled },
      isCanceled,
    );

    const priorReceived = priorLedgerReceived(li, mine);

    const row: CheckableLineItem = {
      line: li.poLineSequence,
      poLineSequence: li.poLineSequence,
      description: li.itemDescription,
      itemNumber: li.itemNumber,
      unit: li.unitOfMeasure,
      quantity: String(quantityExpected ?? li.qtyExpected),
      totalQty: '',
      receivedQty: '',
      status: mine?.status ?? null,
      quantityExpected,
      // THE PRIOR LEDGER, not po-data's rollup. See the file header.
      quantityReceived: priorReceived,
      isClosed: priorLedgerClosed(li, mine, quantityExpected, priorReceived),
      isCanceled,
      isCanceledOriginal: li.isCanceled,
      quantityOrderedOriginal: li.qtyOrdered,
      quantityExpectedOriginal: li.qtyExpected,
      // Every receipt behind the line, this log's included — `isRaggedLine` reads
      // this to tell a parts line from a countable one, and a ragged line's only
      // evidence is a null-quantity row that may well be one of ours.
      receipts: li.receipts ?? [],
      // A close this log performed. On a line it did NOT close this is false, so
      // nothing here can turn into a `completesLine: true` the user never asked
      // for — and equally, un-ticking it is a real decision the save must carry.
      completesLine: (mine?.closingTaps ?? 0) > 0,
    };

    // Parts, not a count: round-tripping the descriptions through the same
    // comma-joined text the mobile step produces is what `splitRaggedParts`
    // expects, and it is also what makes `arrivedQty` return null for this line.
    if (mine && mine.parts.length > 0) {
      row.partsReceived = mine.parts.join(', ');
    } else if (mine?.status === 'partial') {
      // A countable partial: what ARRIVED this delivery, refused/returned units
      // included — the damage is re-applied separately and deducts them again.
      row.receivedQty = String(mine.arrived);
    }

    return row;
  });
}

/**
 * The line's ledger total BEFORE this log — po-data's rollup with this log's own
 * units taken back out.
 *
 * Floored at 0 rather than trusted: the rollup and the details payload are two
 * separate reads, so a concurrent delete could in principle make the subtraction
 * negative, and a negative "already received" would inflate every shortfall
 * computed from it.
 */
export function priorLedgerReceived(
  line: Pick<PoLineItem, 'qtyReceived'>,
  mine: LogLineContribution | undefined,
): number {
  const total = Number(line.qtyReceived ?? 0) || 0;
  return Math.max(total - (mine?.received ?? 0), 0);
}

/**
 * Whether the line was already closed BEFORE this log.
 *
 * po-data's `isClosed` is `mathCloses || anyCompletesLineTap` across every receipt
 * on the line, so it says yes on a line THIS log closed — and `isOutstandingLine`
 * drops closed rows, which would make such a line unreachable from the missing
 * page and impossible to reopen.
 *
 * The math half re-runs against the prior total. The tap half is counted rather
 * than identified, because po-data's per-line receipts carry neither an id nor a
 * log id: if the line has more `completesLine` taps than this log contributed,
 * somebody else closed it and that close is not ours to undo.
 */
export function priorLedgerClosed(
  line: Pick<PoLineItem, 'receipts'>,
  mine: LogLineContribution | undefined,
  quantityExpected: number | null,
  priorReceived: number,
): boolean {
  const totalTaps = (line.receipts ?? []).filter((r) => r.completesLine === true).length;
  const otherTaps = Math.max(totalTaps - (mine?.closingTaps ?? 0), 0);
  if (otherTaps > 0) { return true; }
  return quantityExpected != null && quantityExpected > 0 && priorReceived >= quantityExpected;
}

/**
 * This log's OFF-PO receipt rows as their own check-off rows.
 *
 * Each keeps its own row (they are not a PO line and never group) and gets a
 * synthetic NEGATIVE `line`, the same convention the mobile Items step uses for
 * off-PO adds so `lineKey` stays unique across the whole set. `isClosed: true`
 * and `isOffPo: true` both keep them off the missing page, which is right: an
 * off-PO item has no ordered quantity to fall short of.
 */
export function seedOffPoRows(
  receipts: readonly ReceivingLogEditReceipt[] | null | undefined,
): CheckableLineItem[] {
  const rows: CheckableLineItem[] = [];
  let syntheticLine = 0;

  for (const row of receipts ?? []) {
    if (row.poLineSequence != null) { continue; }
    syntheticLine--;
    rows.push({
      line: syntheticLine,
      poLineSequence: null,
      description: row.itemDescription ?? '',
      itemNumber: row.itemNumber ?? '',
      unit: row.unitOfMeasure ?? '',
      // The arrival lives in `quantity` and NOWHERE ELSE. `quantityReceived` is
      // deliberately left unset: on a PO line it means "the ledger before this
      // delivery", and `buildOffPoRow` reads it in preference to `quantity`, so
      // seeding it here would both overload the field and make the amount
      // un-editable (an edit to `quantity` would be silently ignored).
      quantity: row.quantityReceived != null ? String(row.quantityReceived) : '',
      totalQty: '',
      receivedQty: '',
      status: 'complete',
      completesLine: false,
      isOffPo: true,
      isClosed: true,
    });
  }

  return rows;
}

/**
 * The damage already recorded on this log, keyed by `lineKey` — the same lookup
 * `buildItemsString` and the missing-page rules read.
 *
 * READ-ONLY by design. It is seeded so the recomposed note keeps its
 * "(DAMAGED, …)" qualifiers and the screen can show what was reported, not so the
 * screen can change it: sending `damagedQuantity` makes the backend re-derive
 * `isDamaged` from it, so a `0` clears a damage flag nobody mentioned.
 *
 * A ragged line spreads one damage report across several part rows, so the
 * quantity is summed and the first non-empty disposition/note wins — the submit
 * stamps the same values onto every row a damaged line produced, so there is only
 * ever one distinct value to pick.
 */
export function seedDamageByLine(
  rows: readonly CheckableLineItem[],
  contributions: LogContributionsByLine,
  offPoRows: readonly CheckableLineItem[] = [],
  offPoReceipts: readonly ReceivingLogEditReceipt[] | null | undefined = null,
): Record<string, LineDamage> {
  const damageByLine: Record<string, LineDamage> = {};

  for (const row of rows) {
    if (row.poLineSequence == null) { continue; }
    const damage = damageFor(contributions.get(row.poLineSequence)?.rows ?? []);
    if (damage) { damageByLine[lineKey(row)] = damage; }
  }

  // Off-PO rows were built in payload order from the same filtered array, so the
  // nth seeded row corresponds to the nth off-PO receipt.
  const offPo = (offPoReceipts ?? []).filter((r) => r.poLineSequence == null);
  offPoRows.forEach((row, index) => {
    const source = offPo[index];
    if (!source) { return; }
    const damage = damageFor([source]);
    if (damage) { damageByLine[lineKey(row)] = damage; }
  });

  return damageByLine;
}

